/**
 * CaseIngestionResultsSection
 *
 * Displays case ingestion results in the Initial Review modal:
 * 1. Staff notes created during case ingestion (Intake Notes & General Notes)
 * 2. Sections marked as "not found" during ingestion
 *
 * Only renders if a CaseIngestionResult record exists for the matter.
 * Follows Enterprise Compact design from docs/INITIAL_REVIEW_UX.md
 */
import { AlertCircle, ChevronDown, FileEdit, FileSearch } from 'lucide-react';
import { useMemo } from 'react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useListArtifactsQuery } from '@/services/api/artifactService';
import { useGetCaseIngestionQuery } from '@/services/api/caseIngestionService';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import type { ArtifactUnpacked, StaffNote } from '@/services/types/artifact-types';
import {
  INGESTION_SECTIONS,
  type IngestionSection,
} from '@/services/types/case-ingestion-types';

import {
  GENERAL_NOTES_HEADER,
  INTAKE_NOTES_HEADER,
} from '../../case-ingestion/utils/notesParser';

interface CaseIngestionResultsSectionProps {
  matterId: string;
  onDocumentClick?: (artifactId: string) => void;
}

/** Type guard to check if artifact is a staff note */
const isStaffNoteArtifact = (
  artifact: ArtifactUnpacked,
): artifact is ArtifactUnpacked & {
  artifact_type: typeof ArtifactType.STAFF_NOTE;
  staff_note: StaffNote;
} => artifact.artifact_type === ArtifactType.STAFF_NOTE;

/** Human-readable labels for ingestion sections */
const SECTION_LABELS: Record<IngestionSection, string> = {
  first_party_coverage: 'First Party Coverage',
  third_party_coverage: 'Third Party Coverage',
  health_insurance: 'Health Insurance',
  police_report: 'Police Report',
  medical_providers: 'Medical Providers',
  retainer_document: 'Retainer Agreement',
  hipaa_document: 'HIPAA Directive',
  companion_cases: 'Companion Cases',
};

export const CaseIngestionResultsSection = ({
  matterId,
  onDocumentClick,
}: CaseIngestionResultsSectionProps) => {
  // Fetch case ingestion record (returns 404 if none exists)
  const {
    data: caseIngestion,
    isLoading: isIngestionLoading,
    isError: isIngestionError,
  } = useGetCaseIngestionQuery(matterId, { skip: !matterId });

  // Only fetch artifacts if we have a case ingestion record
  const { data: artifacts = [], isLoading: isArtifactsLoading } =
    useListArtifactsQuery(
      { matterId },
      { skip: !matterId || isIngestionError || !caseIngestion },
    );

  // Find case ingestion staff notes
  const ingestionNotes = useMemo(() => {
    const staffNotes = artifacts.filter(isStaffNoteArtifact);

    return {
      intakeNote: staffNotes.find((a) =>
        a.staff_note.content.startsWith(INTAKE_NOTES_HEADER),
      ),
      generalNote: staffNotes.find((a) =>
        a.staff_note.content.startsWith(GENERAL_NOTES_HEADER),
      ),
    };
  }, [artifacts]);

  // Get sections marked as "not_found"
  const notFoundSections = useMemo(() => {
    if (!caseIngestion?.sections) return [];

    return INGESTION_SECTIONS.filter(
      (section) => caseIngestion.sections[section] === 'not_found',
    );
  }, [caseIngestion?.sections]);

  // Loading state
  if (isIngestionLoading || isArtifactsLoading) {
    return (
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3">
        <div className="flex items-center gap-2">
          <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-zinc-300 dark:border-zinc-600 border-t-zinc-600 dark:border-t-zinc-400" />
          <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
            Loading ingestion results...
          </span>
        </div>
      </div>
    );
  }

  // Don't render if no case ingestion record exists (404)
  if (isIngestionError || !caseIngestion) {
    return null;
  }

  // Don't render if there's nothing to show
  const hasStaffNotes =
    ingestionNotes.intakeNote || ingestionNotes.generalNote;
  const hasNotFoundSections = notFoundSections.length > 0;

  if (!hasStaffNotes && !hasNotFoundSections) {
    return null;
  }

  // Extract IDs for click handlers (avoids non-null assertions in JSX)
  const intakeNoteId = ingestionNotes.intakeNote?.id;
  const generalNoteId = ingestionNotes.generalNote?.id;

  return (
    <Collapsible defaultOpen>
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 overflow-hidden">
        {/* Header */}
        <CollapsibleTrigger
          className={cn(
            'flex items-center justify-between w-full px-3 py-2',
            'bg-white dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-800',
            'hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors',
          )}
        >
          <div className="flex items-center gap-1.5">
            <FileSearch className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-xs font-semibold text-zinc-900 dark:text-white">
              Case Ingestion Results
            </h3>
          </div>
          <ChevronDown className="h-3 w-3 text-zinc-400 transition-transform [[data-state=open]>&]:rotate-180" />
        </CollapsibleTrigger>

        {/* Content */}
        <CollapsibleContent>
          <div className="p-2 space-y-3">
            {/* Ingestion Notes - no subsection label needed */}
            {hasStaffNotes && (
              <div className="space-y-1">
                {intakeNoteId && (
                  <button
                    type="button"
                    onClick={() => onDocumentClick?.(intakeNoteId)}
                    className="flex w-full items-center gap-2 text-left rounded px-2 py-1.5 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  >
                    <FileEdit className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="truncate text-xs font-medium text-zinc-900 dark:text-white">
                      Intake Notes
                    </span>
                  </button>
                )}
                {generalNoteId && (
                  <button
                    type="button"
                    onClick={() => onDocumentClick?.(generalNoteId)}
                    className="flex w-full items-center gap-2 text-left rounded px-2 py-1.5 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  >
                    <FileEdit className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="truncate text-xs font-medium text-zinc-900 dark:text-white">
                      General Notes
                    </span>
                  </button>
                )}
              </div>
            )}

            {/* Not Found Subsection */}
            {hasNotFoundSections && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-zinc-600 dark:text-zinc-400 px-2">
                  Not Found During Manual Ingestion
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 px-2">
                  Document sync and extraction may still identify these. Refer
                  to actual matter data for the latest status.
                </p>
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800 px-3 py-2">
                  <ul className="space-y-1">
                    {notFoundSections.map((section) => (
                      <li
                        key={section}
                        className="text-xs font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1.5"
                      >
                        <AlertCircle className="h-3 w-3" />
                        {SECTION_LABELS[section]}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
